import React from 'react';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import { useUserAgent } from 'next-useragent';
import nookies from 'nookies';
import { Else, If, Then } from 'react-if';
import {
  RootStoreProvider,
  useDeparturesStore,
  useAuthStore,
} from '../providers/RootStoreProvider';
import { Layout } from '../components/Layout';
import {
  SearchDepartures,
  plusUnitToDate,
} from '../components/DeparturesPage/SearchDepartures';
import { Text } from '../components/Text';
import styles from '../styles/departuresPage.module.sass';
import { Table } from '../components/Administration/Table';
import { EmptySearch } from '../components/SearchCruises/EmptySearch';
import { List } from '../components/DeparturesPage/List';
import { apiProxy } from '../lib/api';
import { SearchStoreProvider } from '../providers/SearchStoreProvider';
import { BreadCrumbs } from '../components/BreadCrumbs';
import { logger } from '../lib/logger';
import { containsSea } from '../utils/containSea';
// import { SubscribeForm } from '../components/Layout/components/Footer/Footer';

const PageContent = observer(({ isMobile }) => {
  const store = useDeparturesStore();
  const authStore = useAuthStore();

  React.useEffect(() => {
    store.fetchByDate(plusUnitToDate());
  }, []);

  return (
    <>
      <SearchDepartures className={styles.search} />
      <If condition={store?.tableItems?.length > 0}>
        <Then>
          <Table
            className={styles.table}
            headers={store.headers}
            items={store.tableItems}
            loading={store.loading}
            grid={
              authStore.isManager ? 'repeat(4, 1fr) 72px' : 'repeat(4, 1fr)'
            }
            transfer={store.transferItems}
            isMobile={isMobile}
          />
          <List
            className={styles.list}
            headers={store.headers}
            items={store.tableItems}
            loading={store.loading}
            isMobile={isMobile}
          />
        </Then>
        <Else>
          <EmptySearch />
        </Else>
      </If>
    </>
  );
});

export default function DeparturesPage({
  uaString,
  store: { user, popularPopdirs },
  canonicalUrl,
}) {
  const ua = useUserAgent(uaString || window.navigator.userAgent);
  const { isMobile, isTablet } = ua;
  const pageRoute = [{ title: `Онлайн-табло отправлений`, link: '' }];

  return (
    <SearchStoreProvider hydrationData={{ user }}>
      <RootStoreProvider hydrationData={{ user }}>
        <NextSeo
          title="Онлайн-табло отправлений"
          description="Онлайн-табло отправлений"
          additionalLinkTags={[
            {
              rel: 'shortcut icon',
              href: '/favicon.ico?v=2',
            },
          ]}
          canonical={canonicalUrl}
        />
        <Layout
          isMobile={isMobile}
          isTablet={isTablet}
          emailMetrik={'online_tablo_departures_subscribe_email'}
          popularPopdirs={popularPopdirs}
        >
          <div className="container--block">
            <div>
              <BreadCrumbs routeCrumbs={pageRoute} />
            </div>
            <Text
              className={styles.title}
              component="h1"
              variant={isMobile ? 'heading-4' : 'heading-1'}
            >
              Онлайн-табло отправлений
            </Text>
            <PageContent isMobile={isMobile} />
          </div>
        </Layout>
      </RootStoreProvider>
    </SearchStoreProvider>
  );
}

export const getServerSideProps = async ctx => {
  const cookies = nookies.get(ctx);
  const { token } = cookies;

  const canonicalUrl = `https://kruiz.online${ctx.req.url}`;
  const hasSea = containsSea(ctx.req.url);
  try {
    const responses = await Promise.allSettled([
      apiProxy.get(`ApiUsers/check_token/${token ?? ''}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
      apiProxy.get(`ApiPopdirNew/getHeaderPopdir/${hasSea}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
    ]);

    const [user, popularPopdirs] = responses.map(req =>
      req.status === 'fulfilled' ? req.value.data ?? null : null,
    );
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      level: 'debug',
      ym_uid: cookies._ym_uid,
      message: 'departures page has been created',
    });
    return {
      props: {
        store: { user, popularPopdirs },
        uaString: ctx.req.headers['user-agent'],
        canonicalUrl,
      },
    };
  } catch (error) {
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      error,
      level: 'error',
      message: 'departures page was not created',
    });
    return {
      redirect: {
        destination: '/503',
        permanent: true,
      },
    };
  }
};
